import { useId, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { type DiffType, diffCounts, diffLines } from "../lib/diff";

const ORIGINAL =
  "Roses are red\nViolets are blue\nToolverse is fast\nAnd so are you";
const CHANGED =
  "Roses are red\nViolets are blue\nToolverse is fast and free\nAnd so are you\nThanks for visiting";

const ROW_CLASS: Record<DiffType, string> = {
  equal: "text-muted-foreground",
  add: "bg-emerald-500/10 text-emerald-300",
  remove: "bg-rose-500/10 text-rose-300",
};
const SIGN: Record<DiffType, string> = { equal: " ", add: "+", remove: "-" };

export function DiffTool() {
  const uid = useId();
  const [original, setOriginal] = useState(ORIGINAL);
  const [changed, setChanged] = useState(CHANGED);

  const ops = useMemo(() => diffLines(original, changed), [original, changed]);
  const counts = useMemo(() => diffCounts(ops), [ops]);
  // Stable keys without using the array index (lines can repeat).
  const keyedOps = useMemo(() => {
    const seen: Record<string, number> = {};
    return ops.map((op) => {
      const base = `${op.type}|${op.line}`;
      seen[base] = (seen[base] ?? 0) + 1;
      return { ...op, key: `${base}|${seen[base]}` };
    });
  }, [ops]);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-a`}>{m.td_original_label()}</Label>
          <textarea
            id={`${uid}-a`}
            value={original}
            onChange={(e) => setOriginal(e.target.value)}
            spellCheck={false}
            className="min-h-[12rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${uid}-b`}>{m.td_changed_label()}</Label>
          <textarea
            id={`${uid}-b`}
            value={changed}
            onChange={(e) => setChanged(e.target.value)}
            spellCheck={false}
            className="min-h-[12rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card/60 p-4 sm:p-6">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold tracking-tight">
            {m.td_result_label()}
          </h2>
          <div className="flex gap-2 text-xs font-medium">
            <span
              data-testid="td-added"
              className="rounded-full bg-emerald-500/15 px-2.5 py-0.5 text-emerald-300"
            >
              {m.td_added({ n: String(counts.added) })}
            </span>
            <span
              data-testid="td-removed"
              className="rounded-full bg-rose-500/15 px-2.5 py-0.5 text-rose-300"
            >
              {m.td_removed({ n: String(counts.removed) })}
            </span>
          </div>
        </div>
        {keyedOps.length === 0 ? (
          <p className="text-sm text-muted-foreground">{m.td_empty()}</p>
        ) : (
          <div
            data-testid="td-result"
            className="overflow-x-auto rounded-lg bg-background p-3 font-mono text-sm"
          >
            {keyedOps.map((op) => (
              <div
                key={op.key}
                className={`whitespace-pre-wrap px-1 ${ROW_CLASS[op.type]}`}
              >
                <span className="select-none opacity-60">{SIGN[op.type]} </span>
                {op.line || " "}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
